import { Formik } from 'formik';
import keycode from 'keycode';
import EmailIcon from '@mui/icons-material/Email';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import * as Yup from 'yup';

import CardText from 'components/Card/Text';
import CardTitle from 'components/Card/Title';
import auth from 'lib/auth';
import store from 'lib/store';
import { Button } from '@mui/material';
import PrimaryButton from './PrimaryButton';
import SocialButtons from './SocialButtons';

const Login = ({ loading, onForgot, onEmail, onLoading, onRegister, onStateChange, showLoyaltyMessage, setShowLoyaltyMessage }) => {
  const [error, setError] = useState(null);
  const onEnterPress = useCallback(
    (e, enabled, cb) => {
      if (enabled) {
        const key = keycode(e);
        if (key === 'enter' || key === 'return') {
          cb();
        }
      }
    },
    [],
  );
  useEffect(() => {
    return () => setShowLoyaltyMessage(false);
  }, [setShowLoyaltyMessage]);

  return (
    <Formik
      initialValues={useMemo(
        () => ({
          email: '',
          password: '',
        }),
        [],
      )}
      onSubmit={useCallback(
        async ({ email, password }, { setSubmitting }) => {
          onEmail(email);
          onLoading(true);
          const { message, state } = await auth.login(email, password);
          if (state === 'invalid') {
            onLoading(false);
            setError(message);
          } else if (showLoyaltyMessage) {
              window.location.reload('/loyalty/rewards');
            } else {
              onStateChange(state, message);
            }
          setSubmitting(false);
        },
        [
          onEmail,
          onLoading,
          onStateChange,
          showLoyaltyMessage,
        ],
      )}
      validationSchema={useMemo(
        () => Yup
          .object()
          .shape({
            email: Yup.string()
              .trim()
              .lowercase()
              .email('Email must be valid')
              .required('Email is required'),
            password: Yup.string()
              .trim()
              .required('Password is required'),
          }),
        [],
      )}
    >
      {useCallback(
        ({
          dirty,
          errors,
          isSubmitting,
          isValid,
          setFieldTouched,
          setFieldValue,
          submitForm,
          touched,
          values,
        }) => {
          const busy = isSubmitting || loading;
          const canSubmit = !busy && dirty && isValid;
          return (
            <>
              {showLoyaltyMessage
                && (
                <div style={{ padding: '16px', textAlign: 'center' }}>
                  Ooh, we&apos;re excited that you&apos;re curious about our new Loyalty Program! Please sign-in below first to explore the program
                </div>
                )}
              <CardText noTopPadding>
                <Button
                  variant='contained'
                  fullWidth
                  style={{ color: 'white', textTransform: 'initial', marginTop: 5 }}
                  color='primary'
                  onClick={onRegister}
                >
                  Don&apos;t have a Ripple Street Account? Sign up here!
                </Button>
              </CardText>
              <CardTitle title='Sign In' />
              <SocialButtons label='Sign in' />
              <CardText noTopPadding>
                <TextField
                  disabled={busy}
                  errorText={touched?.email && errors?.email}
                  label='Email'
                  fullWidth
                  onBlur={() => setFieldTouched('email')}
                  onChange={(e, value) => {
                    setError(null);
                    setFieldValue('email', value);
                  }}
                  onKeyUp={e => onEnterPress(e, canSubmit, submitForm)}
                  value={values.email}
                />
                <TextField
                  disabled={busy}
                  error={touched?.password && errors?.password}
                  helperText={touched?.password && errors?.password}
                  label='Password'
                  fullWidth
                  type='password'
                  onBlur={() => setFieldTouched('password')}
                  onChange={(e, value) => {
                    setError(null);
                    setFieldValue('password', value);
                  }}
                  onKeyUp={e => onEnterPress(e, canSubmit, submitForm)}
                  value={values.password}
                />
              </CardText>
              <CardText noTopPadding>
                <Button
                  variant='contained'
                  fullWidth
                  style={{ color: 'white', textTransform: 'initial' }}
                  color='primary'
                  onClick={onForgot}
                >
                  I forgot my password
                </Button>
              </CardText>
              <PrimaryButton
                error={error}
                disabled={!canSubmit}
                fullWidth
                icon={<EmailIcon />}
                label='Sign in with email'
                loading={busy}
                onClick={async () => {
                  await store.save('skipUntilNextLogin', false);
                  submitForm();
}}
              />
            </>
          );
        },
        [
          error,
          loading,
          onEnterPress,
          onForgot,
          onRegister,
          showLoyaltyMessage,
        ],
      )}
    </Formik>
  );
};

Login.propTypes = {
  loading: PropTypes.bool,
  onForgot: PropTypes.func.isRequired,
  onEmail: PropTypes.func.isRequired,
  onLoading: PropTypes.func.isRequired,
  onRegister: PropTypes.func.isRequired,
  onStateChange: PropTypes.func.isRequired,
  showLoyaltyMessage: PropTypes.bool.isRequired,
  setShowLoyaltyMessage: PropTypes.func.isRequired,
};

export default Login;
